import { useNavigate } from 'react-router-dom';
import { postUploadTeamLogo } from '@/api';
import { ImageCropUploadScreen } from '@/components/ui/ImageCropUploadScreen';

/** Square previews — team logo is used on invoices and must stay 1:1. */
export const TEAM_LOGO_PREVIEW_SIZES = [
  {
    width: 128,
    height: 128,
    label: 'Large',
    badgeClass: 'bg-green-500',
    imageClass: 'rounded-lg object-cover border-4 border-white shadow-md',
  },
  {
    width: 64,
    height: 64,
    label: 'Medium',
    badgeClass: 'bg-gray-500',
    imageClass: 'rounded-lg object-cover border-2 border-white shadow',
  },
  {
    width: 36,
    height: 36,
    label: 'Small',
    badgeClass: 'bg-gray-400',
    imageClass: 'rounded-lg object-cover border border-white shadow',
  },
] as const;

type TeamLogoUploadPageProps = {
  /** When true, only upload file and return path (create-team draft). Default returns to team settings. */
  draftOnly?: boolean;
  onDraftSuccess?: (logoPath: string) => void;
  onDraftBack?: () => void;
};

export default function TeamLogoUploadPage({
  draftOnly = false,
  onDraftSuccess,
  onDraftBack,
}: TeamLogoUploadPageProps = {}) {
  const navigate = useNavigate();

  return (
    <ImageCropUploadScreen
      titleSelect="Team Logo"
      titleCrop="Crop Team Logo"
      titlePreview="Preview Team Logo"
      uploadPrompt="Upload a square team logo"
      uploadHint={
        'Used on invoices — must be square (1:1).\nJPG, PNG or WEBP up to 5MB.'
      }
      previewHeading="Invoice & profile preview"
      previewSubheading="Your logo will appear square on invoices and team pages"
      saveLabel="Save Logo"
      confirmLabel="Confirm & Save"
      aspect={1}
      cropShape="rect"
      previewSizes={[...TEAM_LOGO_PREVIEW_SIZES]}
      onUpload={(blob) => postUploadTeamLogo(blob)}
      onSuccess={(uploadedUrl) => {
        if (draftOnly) {
          onDraftSuccess?.(uploadedUrl);
          return;
        }
        navigate('/team/settings', {
          replace: true,
          state: { logoPath: uploadedUrl, logoSaved: true },
        });
      }}
      onBack={() => {
        if (draftOnly) {
          onDraftBack?.();
          return;
        }
        navigate('/team/settings');
      }}
    />
  );
}
